import { FC, useEffect, useMemo, useState } from 'react';

import { getChannelBalances } from '@shared/api/giftChannelLedger/giftChannelLedger.controller';
import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import { GiftCouponForRedemption } from '@shared/api/giftCoupon/giftCoupon.interface';

import {
  HOLD_SUGGEST_UNDELIVER_COUNT,
  estimateUndeliverByChannel,
  shouldSuggestHold,
  undeliverCountOf,
} from '../utils/undeliver.utils';

/** 사유 프리셋 — 키워드 택1 후 필요 시 수정 (원장 memo·쿠폰 기록에 남음) */
const REASON_PRESETS = ['매체 발송 실패', '실수 처리 정정'] as const;

interface Props {
  coupons: GiftCouponForRedemption[];
  onClose: () => void;
  /** true = 처리 완료 (부모가 모달 닫기 + 선택 해제) */
  onConfirm: (reason: string, hold: boolean) => Promise<boolean>;
}

/**
 * 발송완료 취소 확인 모달 — 운영자용이라 설명문 없이 키워드 요약만.
 * 모노톤(gray) + 확정 버튼만 rose 액센트.
 */
export const UndeliverModal: FC<Props> = ({ coupons, onClose, onConfirm }) => {
  const [reason, setReason] = useState<string>(REASON_PRESETS[0]);
  const [isProcessing, setIsProcessing] = useState(false);

  // 취소 후 상태 — 발송대기 복귀(기본) vs 발송보류(나중에 처리).
  // 반복 취소(이번 포함 5회 이상)가 섞여 있으면 보류를 기본값으로 연다.
  const suggestHold = useMemo(() => shouldSuggestHold(coupons), [coupons]);
  const [hold, setHold] = useState(suggestHold);
  const maxUndeliverCount = useMemo(
    () => coupons.reduce((max, c) => Math.max(max, undeliverCountOf(c)), 0),
    [coupons],
  );

  // 원장 잔액 — 열 때마다 새로 조회해 "잔액 → 취소 후" 대조 표기.
  // 구서버(404)·조회 실패면 null 유지, 잔액 줄만 조용히 생략
  const [balances, setBalances] = useState<ChannelBalanceSummary[] | null>(null);
  useEffect(() => {
    let alive = true;
    getChannelBalances()
      .then((b) => {
        if (alive) setBalances(b);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const estimates = useMemo(() => estimateUndeliverByChannel(coupons), [coupons]);
  const refundSum = estimates.reduce((sum, e) => (e.isUnknown ? sum : sum + e.sum), 0);

  const trimmedReason = reason.trim();
  const canConfirm = trimmedReason.length > 0 && !isProcessing;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsProcessing(true);
    try {
      await onConfirm(trimmedReason, hold);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={() => !isProcessing && onClose()}
      />
      <div className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-baseline justify-between">
          <h3 className="text-base font-bold text-gray-900">발송취소</h3>
          <span className="text-sm font-semibold text-gray-500">
            {coupons.length}건
          </span>
        </div>

        {/* 요약 — 키워드 행 */}
        <dl className="mt-4 space-y-2 border-y border-gray-100 py-3">
          <div className="flex items-start gap-3">
            <dt className="w-10 shrink-0 pt-0.5 text-xs font-semibold text-gray-500">
              환급
            </dt>
            <dd className="flex flex-1 flex-col gap-1.5">
              {estimates.map((e) => {
                const balance =
                  e.channel && balances
                    ? balances.find((b) => b.channel === e.channel) ?? null
                    : null;
                return (
                  <div key={e.label}>
                    <div className="flex items-center justify-between text-sm">
                      <span
                        className={e.isUnknown ? 'text-gray-500' : 'text-gray-800'}
                      >
                        {e.label}{' '}
                        <span className="text-xs text-gray-500">{e.count}건</span>
                      </span>
                      <span
                        className={
                          e.isUnknown
                            ? 'text-xs text-gray-500'
                            : 'font-bold tabular-nums text-gray-900'
                        }
                      >
                        {e.isUnknown ? '환급 없음' : `+${e.sum.toLocaleString()}원`}
                      </span>
                    </div>
                    {balance && (
                      <div className="text-right text-xs tabular-nums text-gray-600">
                        잔액 {balance.balance.toLocaleString()} →{' '}
                        <span className="font-semibold text-gray-900">
                          {(balance.balance + e.sum).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
              {estimates.length > 1 && refundSum > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-1 text-sm">
                  <span className="text-xs text-gray-500">합계</span>
                  <span className="font-bold tabular-nums text-gray-900">
                    +{refundSum.toLocaleString()}원
                  </span>
                </div>
              )}
            </dd>
          </div>
          <div className="flex items-start gap-3">
            <dt className="w-10 shrink-0 pt-1 text-xs font-semibold text-gray-500">
              복귀
            </dt>
            <dd className="flex-1">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setHold(false)}
                  disabled={isProcessing}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    hold
                      ? 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                      : 'border-gray-900 bg-gray-900 text-white'
                  }`}
                >
                  발송대기
                </button>
                <button
                  type="button"
                  onClick={() => setHold(true)}
                  disabled={isProcessing}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    hold
                      ? 'border-amber-600 bg-amber-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  발송보류
                </button>
                {maxUndeliverCount > 0 && (
                  <span
                    className={`ml-auto rounded px-1.5 py-0.5 text-[11px] font-bold ${
                      suggestHold
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                    title={`선택 건 중 최다 취소 이력 ${maxUndeliverCount}회`}
                  >
                    취소이력 {maxUndeliverCount}회
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {hold
                  ? '발송보류 탭에 남습니다 · 발송대기 목록에서 제외 · 신청정보 유지'
                  : '발송대기 목록으로 · 신청정보 유지'}
              </p>
              {suggestHold && !hold && (
                <p className="mt-0.5 text-xs font-medium text-amber-700">
                  반복 취소 {HOLD_SUGGEST_UNDELIVER_COUNT}회 이상 — 보류 권장
                </p>
              )}
            </dd>
          </div>
        </dl>

        {/* 사유 — 프리셋 칩 + 자유 수정 */}
        <div className="mt-3">
          <div className="flex items-center gap-1.5">
            <span className="w-10 shrink-0 text-xs font-semibold text-gray-500">
              사유
            </span>
            {REASON_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setReason(preset)}
                disabled={isProcessing}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  trimmedReason === preset
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                }`}
              >
                {preset}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isProcessing}
            placeholder="사유 입력"
            className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>

        {/* 액션 */}
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            닫기
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
              hold ? 'bg-amber-600 hover:bg-amber-700' : 'bg-rose-600 hover:bg-rose-700'
            }`}
          >
            {isProcessing
              ? '처리중…'
              : hold
              ? `발송보류 ${coupons.length}건`
              : `발송취소 ${coupons.length}건`}
          </button>
        </div>
      </div>
    </div>
  );
};
